import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity } from '../../utils/security';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ShieldCheck, ExternalLink, Activity, Clock, Calendar, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const AdminUserDetail = () => {
    const { id } = useParams();
    const { user, isSuperAdmin, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [targetUser, setTargetUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!authLoading && !isSuperAdmin()) navigate('/admin');
    }, [authLoading, isSuperAdmin, navigate]);

    const fetchUserDetails = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchWithSecurity(`${API_URL}users.php?id=${id}&with_activity=1`);
            const data = await response.json();
            if (data && !data.error) {
                setTargetUser(data);
            } else {
                toast.error(t('admin.userDetail.toastNotFound') || 'მომხმარებელი ვერ მოიძებნა');
                navigate('/admin/users');
            }
        } catch (error) {
            toast.error(t('admin.userDetail.toastLoadError') || 'აქტივობის ჩატვირთვის შეცდომა');
        }
        setLoading(false);
    }, [id, t, navigate]);

    useEffect(() => {
        if (user && id) fetchUserDetails();
    }, [user, id, fetchUserDetails]);

    if (authLoading || !user) return null;

    return (
        <AdminLayout title={t('admin.userDetail.title') || 'მომხმარებლის პროფილი & აქტივობის ლოგი'}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <button className="shadcn-btn shadcn-btn-secondary" onClick={() => navigate('/admin/users')}>
                    <ArrowLeft size={16} /> {t('common.back') || 'მომხმარებლებში დაბრუნება'}
                </button>
                {targetUser && (
                    <span className="shadcn-badge shadcn-badge-primary">
                        <ShieldCheck size={14} /> {targetUser.role.toUpperCase().replace('_', ' ')}
                    </span>
                )}
            </div>

            {loading ? (
                <div className="text-center py-5 text-muted">
                    <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                    <span>{t('admin.users.loading') || 'მონაცემები იტვირთება...'}</span>
                </div>
            ) : targetUser ? (
                <div className="row g-4">
                    {/* Profile Card */}
                    <div className="col-lg-4">
                        <div className="shadcn-card text-center h-100">
                            <div className="mb-4">
                                {targetUser.profile_picture ? (
                                    <img
                                        src={targetUser.profile_picture.startsWith('http') ? targetUser.profile_picture : API_URL + targetUser.profile_picture}
                                        alt={targetUser.username}
                                        className="avatar-circle mx-auto mb-3"
                                        style={{ width: '96px', height: '96px' }}
                                    />
                                ) : (
                                    <div className="avatar-circle-icon-lg mb-3">
                                        <User size={44} />
                                    </div>
                                )}
                                <h3 className="text-dark fs-4 fw-bold mb-1">{targetUser.username}</h3>
                                <p className="text-muted small mb-0">{t('admin.userDetail.userId', { id: targetUser.id }) || `ID: #${targetUser.id}`}</p>
                            </div>

                            <div className="row g-2 mb-4">
                                <div className="col-6">
                                    <div className="p-3 rounded-3 bg-slate-50 border">
                                        <div className="fs-4 fw-bold text-primary">
                                            {targetUser.portfolio_views?.length || 0}
                                        </div>
                                        <div className="text-muted smallest">{t('admin.userDetail.projectViews') || 'პროექტების ნახვები'}</div>
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className="p-3 rounded-3 bg-slate-50 border">
                                        <div className="fs-4 fw-bold text-info">
                                            {targetUser.blog_views?.length || 0}
                                        </div>
                                        <div className="text-muted smallest">{t('admin.userDetail.blogReads') || 'ბლოგის კითხვები'}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-3 border-top text-start">
                                <div className="d-flex justify-content-between mb-2 small">
                                    <span className="text-muted d-flex align-items-center gap-1"><Calendar size={13} /> {t('admin.userDetail.registered') || 'რეგისტრირებულია:'}</span>
                                    <span className="text-dark fw-semibold">{new Date(targetUser.created_at).toLocaleDateString()}</span>
                                </div>
                                <div className="d-flex justify-content-between small">
                                    <span className="text-muted d-flex align-items-center gap-1"><Clock size={13} /> {t('admin.userDetail.lastActivity') || 'ბოლო აქტივობა:'}</span>
                                    <span className="text-dark fw-semibold">
                                        {targetUser.portfolio_views?.[0]?.viewed_at
                                            ? new Date(targetUser.portfolio_views[0].viewed_at).toLocaleDateString()
                                            : (t('admin.userDetail.noActivity') || 'აქტივობა არ არის')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Activity Feed */}
                    <div className="col-lg-8">
                        <div className="shadcn-card h-100">
                            <div className="shadcn-card-header mb-4">
                                <div className="shadcn-card-title">
                                    <Activity size={20} className="text-primary" />
                                    <span>{t('admin.userDetail.activityTimeline') || 'ლოგები'}</span>
                                </div>
                            </div>

                            <h5 className="text-dark fs-6 fw-bold mb-3">{t('admin.userDetail.portfolioViews') || 'პორტფოლიოს ნახვები'}</h5>
                            <div className="d-flex flex-column gap-3 mb-4">
                                {targetUser.portfolio_views && targetUser.portfolio_views.length > 0 ? (
                                    targetUser.portfolio_views.map((act, idx) => (
                                        <div key={idx} className="p-3 rounded-3 bg-white border d-flex justify-content-between align-items-center shadow-sm">
                                            <div>
                                                <div className="smallest text-muted">{new Date(act.viewed_at).toLocaleString()}</div>
                                                <div className="text-dark small fw-medium">
                                                    {t('admin.userDetail.viewedProject') || 'ნანახი პროექტი:'} <strong>{act.entity_title || (t('admin.userDetail.unknownProject') || 'უცნობი პროექტი')}</strong>
                                                </div>
                                            </div>
                                            {act.entity_slug && (
                                                <Link to={`/portfolio/${act.entity_slug}`} className="shadcn-btn shadcn-btn-sm shadcn-btn-secondary" target="_blank">
                                                    {t('common.view') || 'ნახვა'} <ExternalLink size={12} />
                                                </Link>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-muted small py-3 mb-0">{t('admin.userDetail.noPortfolioActivity') || 'პორტფოლიოს აქტივობა არ მოიძებნა.'}</p>
                                )}
                            </div>

                            {targetUser.blog_views && targetUser.blog_views.length > 0 && (
                                <>
                                    <h5 className="text-dark fs-6 fw-bold mb-3 pt-3 border-top">{t('admin.userDetail.blogActivity') || 'ბლოგის აქტივობა'}</h5>
                                    <div className="d-flex flex-column gap-3">
                                        {targetUser.blog_views.map((act, idx) => (
                                            <div key={idx} className="p-3 rounded-3 bg-white border d-flex justify-content-between align-items-center shadow-sm">
                                                <div>
                                                    <div className="smallest text-muted">{new Date(act.viewed_at).toLocaleString()}</div>
                                                    <div className="text-dark small fw-medium">
                                                        {t('admin.userDetail.readArticle') || 'წაკითხული სტატია:'} <strong>{act.entity_title || 'უცნობი სტატია'}</strong>
                                                    </div>
                                                </div>
                                                {act.entity_slug && (
                                                    <Link to={`/blog-single/${act.entity_slug}`} className="shadcn-btn shadcn-btn-sm shadcn-btn-secondary" target="_blank">
                                                        {t('common.read') || 'წაკითხვა'} <ExternalLink size={12} />
                                                    </Link>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            ) : null}
        </AdminLayout>
    );
};

export default AdminUserDetail;
